import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { translateOrDefault } from "../../utils/translationUtils";
import { AltusSourceSettingsApi } from "./AltusSourceSettings.Api";
import type { AltusSourceSettingsViewModel } from "./AltusSourceSettings.ViewModel.types";
import type {
  AltusConfiguration,
  AltusProject,
  AltusProjectBucket,
} from "./AltusSourceSettings.types";

const NONE_OPTION = "__none";
const PROJECT_SEARCH_MIN_LENGTH = 2;
const LOAD_PROJECTS_ERROR_MESSAGE =
  "Failed to load projects. Is this an Altus environment?";
const LOAD_PROJECTS_ERROR_KEY =
  "board.connections.dialog.sourceSettings.altus.loadProjectsError";

const toSelectedOption = (value?: string | null): string => {
  return value && value.length > 0 ? value : NONE_OPTION;
};

const mergeProjects = (
  existingProjects: AltusProject[],
  additionalProjects: AltusProject[],
): AltusProject[] => {
  const projectById = new Map<string, AltusProject>();
  [...existingProjects, ...additionalProjects].forEach((project) => {
    projectById.set(project.sensei_projectid, project);
  });

  return Array.from(projectById.values());
};

const projectsAreEqual = (
  left: AltusProject[],
  right: AltusProject[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((project, index) => {
    const matchingProject = right[index];
    return (
      matchingProject?.sensei_projectid === project.sensei_projectid &&
      matchingProject?.sensei_name === project.sensei_name
    );
  });
};

export const useAltusSourceSettingsViewModel = (
  model: SourceSettingsModel,
): AltusSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const translateRef = useRef(model.t);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );
  const previousEnvironmentUrlRef = useRef<string | null>(null);
  const projectsRef = useRef<AltusProject[]>([]);

  const [projects, setProjects] = useState<AltusProject[]>([]);
  const [buckets, setBuckets] = useState<AltusProjectBucket[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [bucketsLoaded, setBucketsLoaded] = useState(false);
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    translateRef.current = model.t;
  }, [model.t]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Altus && model.editingConnection.isAuthorised;
  const isAuthorisedProfileReady = isVisible && Boolean(model.editingConnection.profileName);

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<AltusConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const selectedProjectIds = (configuration.projects ?? []).map(
    (project) => project.sensei_projectid,
  );
  const selectedProjectIdsKey = selectedProjectIds.join("|");
  const selectedProjectIdForBucketMode =
    selectedProjectIds.length === 1 ? selectedProjectIds[0] : "";
  const isBucketMode = selectedProjectIds.length === 1;
  const hasProjectBuckets = Boolean(configuration.projectBuckets);
  const hasProjectDoneBucket = Boolean(configuration.projectDoneBucket);
  const hasProjectDefaultBucket = Boolean(configuration.projectDefaultBucket);

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      previousEnvironmentUrlRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<AltusConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: cfg.projects ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const currentEnvironmentUrl = configuration.url ?? "";
    if (previousEnvironmentUrlRef.current === null) {
      previousEnvironmentUrlRef.current = currentEnvironmentUrl;
      return;
    }

    if (previousEnvironmentUrlRef.current === currentEnvironmentUrl) {
      return;
    }

    previousEnvironmentUrlRef.current = currentEnvironmentUrl;

    setProjects([]);
    setBuckets([]);
    setBucketsLoaded(true);

    updateConnectionConfiguration<AltusConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: [],
        projectBuckets: null,
        projectDoneBucket: null,
        projectDefaultBucket: null,
      }),
    );
  }, [configuration.url, isVisible]);

  // Reset source selections on auth/profile identity change.
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setProjects([]);
    setBuckets([]);
    setBucketsLoaded(true);

    updateConnectionConfiguration<AltusConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: [],
        projectBuckets: null,
        projectDoneBucket: null,
        projectDefaultBucket: null,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    setProjects((existingProjects) =>
      {
        const mergedProjects = mergeProjects(
          existingProjects,
          configuration.projects ?? [],
        );
        projectsRef.current = mergedProjects;
        return projectsAreEqual(existingProjects, mergedProjects)
          ? existingProjects
          : mergedProjects;
      },
    );
    setProjectsLoaded(true);
  }, [selectedProjectIdsKey, configuration.projects]);

  useEffect(() => {
    const loadProjectBuckets = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !isBucketMode ||
        !model.boardId ||
        !editingConnectionId
      ) {
        setBuckets([]);
        setBucketsLoaded(true);
        return;
      }

      if (!selectedProjectIdForBucketMode) {
        setBuckets([]);
        setBucketsLoaded(true);
        return;
      }

      const altusApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<AltusSourceSettingsApi>(
          SourceTypeIds.Altus,
        );
      if (!altusApi) {
        setBuckets([]);
        setBucketsLoaded(true);
        return;
      }

      setBucketsLoaded(false);
      setHasError(false);
      setErrorMessage(undefined);
      try {
        const loadedBuckets = await altusApi.getProjectBuckets(
          model.boardId,
          editingConnectionId,
          selectedProjectIdForBucketMode,
        );

        setBuckets(loadedBuckets);

        const hasMissingBucketDefaults =
          !hasProjectDoneBucket || !hasProjectBuckets || !hasProjectDefaultBucket;

        if (loadedBuckets.length > 1 && hasMissingBucketDefaults) {
          const defaultDoneBucket = loadedBuckets[loadedBuckets.length - 1] ?? null;
          const defaultLoadBuckets = loadedBuckets.slice(0, loadedBuckets.length - 1);
          const defaultBucket = loadedBuckets[0] ?? null;

          updateConnectionConfiguration<AltusConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            // Fills only missing buckets, and only for the project they were
            // loaded for, so it is safe to apply to the saved settings too.
            (cfg) =>
              cfg.projects?.length !== 1 ||
              cfg.projects[0].sensei_projectid !== selectedProjectIdForBucketMode
                ? cfg
                : {
                    ...cfg,
                    projectDoneBucket: cfg.projectDoneBucket ?? defaultDoneBucket,
                    projectBuckets: cfg.projectBuckets ?? defaultLoadBuckets,
                    projectDefaultBucket: cfg.projectDefaultBucket ?? defaultBucket,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setBuckets([]);
        setHasError(true);
      } finally {
        setBucketsLoaded(true);
      }
    };

    void loadProjectBuckets();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    isBucketMode,
    model.boardId,
    selectedProjectIdForBucketMode,
    hasProjectDoneBucket,
    hasProjectBuckets,
    hasProjectDefaultBucket,
  ]);

  return {
    isVisible,
    isAuthorisedProfileReady,
    environmentDisplayName: configuration.orgDisplayName,
    environmentUrl: configuration.url,
    projects,
    projectsLoaded,
    isSearchingProjects,
    buckets,
    bucketsLoaded,
    hasError,
    errorMessage,
    selectedProjectIds,
    selectedBucketIds: (configuration.projectBuckets ?? []).map(
      (bucket) => bucket.sensei_bucketid,
    ),
    selectedDoneBucketId: toSelectedOption(
      configuration.projectDoneBucket?.sensei_bucketid,
    ),
    selectedDefaultBucketId: toSelectedOption(
      configuration.projectDefaultBucket?.sensei_bucketid,
    ),
    isBucketMode,
    noneOptionValue: NONE_OPTION,

    onSearchProjects: async (term: string): Promise<AltusProject[]> => {
      const trimmedTerm = term.trim();
      if (trimmedTerm.length < PROJECT_SEARCH_MIN_LENGTH) {
        setHasError(false);
        setErrorMessage(undefined);
        return [];
      }

      if (!isAuthorisedProfileReady || !model.boardId || !editingConnectionId) {
        return [];
      }

      const altusApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<AltusSourceSettingsApi>(
          SourceTypeIds.Altus,
        );
      if (!altusApi) {
        return [];
      }

      setIsSearchingProjects(true);
      setProjectsLoaded(false);
      setHasError(false);
      setErrorMessage(undefined);

      try {
        const searchResults = await altusApi.getProjects(
          model.boardId,
          editingConnectionId,
          trimmedTerm,
        );
        const mergedProjects = mergeProjects(projectsRef.current, searchResults);
        projectsRef.current = mergedProjects;
        setProjects((existingProjects) =>
          projectsAreEqual(existingProjects, mergedProjects)
            ? existingProjects
            : mergedProjects,
        );
        return searchResults;
      } catch {
        setHasError(true);
        setErrorMessage(
          translateOrDefault(
            translateRef.current,
            LOAD_PROJECTS_ERROR_KEY,
            LOAD_PROJECTS_ERROR_MESSAGE,
          ),
        );
        return [];
      } finally {
        setIsSearchingProjects(false);
        setProjectsLoaded(true);
      }
    },

    onSelectProjects: (projectIds: string[]) => {
      const projectById = new Map<string, AltusProject>();
      [...projectsRef.current, ...(configuration.projects ?? [])].forEach((project) => {
        projectById.set(project.sensei_projectid, project);
      });

      const selectedProjects = projectIds
        .map((projectId) => projectById.get(projectId))
        .filter((project): project is AltusProject => Boolean(project));

      setBuckets([]);
      setBucketsLoaded(selectedProjects.length !== 1);

      updateConnectionConfiguration<AltusConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projects: selectedProjects,
          projectBuckets: null,
          projectDoneBucket: null,
          projectDefaultBucket: null,
        }),
      );
    },

    onSelectBuckets: (bucketIds: string[]) => {
      const selectedBuckets = buckets.filter((bucket) =>
        bucketIds.includes(bucket.sensei_bucketid),
      );

      updateConnectionConfiguration<AltusConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projectBuckets: selectedBuckets,
        }),
      );
    },

    onSelectDoneBucket: (bucketId?: string) => {
      const selectedDoneBucket =
        buckets.find((bucket) => bucket.sensei_bucketid === bucketId) ?? null;

      updateConnectionConfiguration<AltusConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projectDoneBucket: selectedDoneBucket,
        }),
      );
    },

    onSelectDefaultBucket: (bucketId?: string) => {
      const selectedDefaultBucket =
        buckets.find((bucket) => bucket.sensei_bucketid === bucketId) ?? null;

      updateConnectionConfiguration<AltusConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projectDefaultBucket: selectedDefaultBucket,
        }),
      );
    },
  };
};
